<script setup lang="ts">
import { computed, ref } from 'vue'
import { bytesToMB, formatClock, formatSpeed } from '@/utils/format'
import type { SpeedPoint } from '@/composables/useSpeedHistory'

/**
 * Dual-axis download/upload speed chart over a 60s sliding window.
 *
 * NOTE ON RECHARTS — sa4-task.md asks for Recharts, and `recharts@2.13.3` is in
 * package.json, but Recharts is a **React** component library: it imports `react`
 * / `react-dom` (neither is installed, and this app is Vue 3). Rendering it from
 * an SFC is not possible without adding React + a bridge such as `veaury`, which
 * would pull ~140 kB of runtime into the bundle for a single chart.
 *
 * This component therefore renders the specified chart (dual Y axis, 60s window,
 * area + line, grid, crosshair tooltip) directly in SVG. It is dependency-free,
 * reads its colours from the design tokens so dark mode works automatically, and
 * keeps the exact `{time, down, up}` data contract from the task description.
 */

interface Props {
  data: SpeedPoint[]
  height?: number
  loading?: boolean
  /** axis/legend labels (i18n supplied by the parent) */
  downLabel?: string
  upLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  height: 260,
  loading: false,
  downLabel: 'Download',
  upLabel: 'Upload',
})

// viewBox geometry — the SVG scales responsively, padding is in viewBox units.
const VB_W = 720
const VB_H = 260
const PAD = { top: 16, right: 56, bottom: 26, left: 56 }
const plotW = VB_W - PAD.left - PAD.right
const plotH = VB_H - PAD.top - PAD.bottom

const hoverIndex = ref<number | null>(null)

const points = computed(() => props.data ?? [])

/** Axis maxima in MB/s, rounded up to a "nice" step so gridlines read cleanly. */
function niceMax(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 1
  const exp = Math.floor(Math.log10(raw))
  const base = 10 ** exp
  const n = raw / base
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
  return step * base
}

const maxDownMB = computed(() =>
  niceMax(Math.max(...points.value.map((p) => bytesToMB(p.down)), 0.001)),
)
const maxUpMB = computed(() =>
  niceMax(Math.max(...points.value.map((p) => bytesToMB(p.up)), 0.001)),
)

function xAt(i: number): number {
  const n = points.value.length
  if (n <= 1) return PAD.left
  return PAD.left + (i / (n - 1)) * plotW
}

function yAt(valueMB: number, maxMB: number): number {
  const ratio = maxMB > 0 ? Math.min(valueMB / maxMB, 1) : 0
  return PAD.top + plotH - ratio * plotH
}

function buildLine(key: 'down' | 'up', maxMB: number): string {
  const pts = points.value
  if (pts.length === 0) return ''
  return pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(2)},${yAt(bytesToMB(p[key]), maxMB).toFixed(2)}`)
    .join(' ')
}

function buildArea(key: 'down' | 'up', maxMB: number): string {
  const line = buildLine(key, maxMB)
  if (!line) return ''
  const lastX = xAt(points.value.length - 1)
  const baseY = PAD.top + plotH
  return `${line} L${lastX.toFixed(2)},${baseY} L${PAD.left},${baseY} Z`
}

const downLine = computed(() => buildLine('down', maxDownMB.value))
const downArea = computed(() => buildArea('down', maxDownMB.value))
const upLine = computed(() => buildLine('up', maxUpMB.value))
const upArea = computed(() => buildArea('up', maxUpMB.value))

/** 5 horizontal gridlines ⇒ 4 intervals. */
const GRID_ROWS = 4
const gridLines = computed(() =>
  Array.from({ length: GRID_ROWS + 1 }, (_, i) => {
    const y = PAD.top + (i / GRID_ROWS) * plotH
    const frac = 1 - i / GRID_ROWS
    return {
      y,
      leftLabel: (maxDownMB.value * frac).toFixed(maxDownMB.value < 10 ? 1 : 0),
      rightLabel: (maxUpMB.value * frac).toFixed(maxUpMB.value < 10 ? 1 : 0),
    }
  }),
)

/** x-axis ticks at both ends and the middle. */
const xTicks = computed(() => {
  const pts = points.value
  if (pts.length < 2) return []
  const idxs = [0, Math.floor((pts.length - 1) / 2), pts.length - 1]
  return idxs.map((i) => ({ x: xAt(i), label: formatClock(pts[i].ts) }))
})

const hovered = computed(() => {
  const i = hoverIndex.value
  if (i === null || !points.value[i]) return null
  const p = points.value[i]
  return {
    x: xAt(i),
    downY: yAt(bytesToMB(p.down), maxDownMB.value),
    upY: yAt(bytesToMB(p.up), maxUpMB.value),
    time: formatClock(p.ts),
    down: formatSpeed(p.down),
    up: formatSpeed(p.up),
    /** flip the tooltip to the left half when near the right edge */
    flip: xAt(i) > PAD.left + plotW * 0.62,
  }
})

function onMove(ev: MouseEvent): void {
  const target = ev.currentTarget as SVGSVGElement | null
  if (!target || points.value.length === 0) return
  const rect = target.getBoundingClientRect()
  if (rect.width === 0) return
  // map client px → viewBox units → nearest sample index
  const vbX = ((ev.clientX - rect.left) / rect.width) * VB_W
  const ratio = (vbX - PAD.left) / plotW
  const i = Math.round(ratio * (points.value.length - 1))
  hoverIndex.value = Math.max(0, Math.min(points.value.length - 1, i))
}

function onLeave(): void {
  hoverIndex.value = null
}
</script>

<template>
  <!-- v3.13.81: 窄屏横向滚动 + 最小宽度 — preserveAspectRatio=none 会把
       坐标轴文字非等比压到不可读, 横滑比压扁好 -->
  <div class="relative w-full overflow-x-auto" :style="{ height: `${height}px` }">
    <div class="relative h-full" :style="{ minWidth: '560px' }">
    <svg
      class="h-full w-full touch-none"
      :viewBox="`0 0 ${VB_W} ${VB_H}`"
      preserveAspectRatio="none"
      role="img"
      :aria-label="`${downLabel} / ${upLabel}`"
      @mousemove="onMove"
      @mouseleave="onLeave"
    >
      <defs>
        <linearGradient id="sc-down-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="hsl(var(--brand-500))" stop-opacity="0.35" />
          <stop offset="100%" stop-color="hsl(var(--brand-500))" stop-opacity="0.02" />
        </linearGradient>
        <linearGradient id="sc-up-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="hsl(var(--success))" stop-opacity="0.28" />
          <stop offset="100%" stop-color="hsl(var(--success))" stop-opacity="0.02" />
        </linearGradient>
      </defs>

      <!-- grid + left/right axis labels -->
      <g>
        <line
          v-for="(g, i) in gridLines"
          :key="`g${i}`"
          :x1="PAD.left"
          :x2="PAD.left + plotW"
          :y1="g.y"
          :y2="g.y"
          stroke="hsl(var(--border))"
          stroke-width="1"
          :stroke-dasharray="i === gridLines.length - 1 ? '0' : '3 3'"
          vector-effect="non-scaling-stroke"
        />
        <text
          v-for="(g, i) in gridLines"
          :key="`l${i}`"
          :x="PAD.left - 8"
          :y="g.y + 3"
          text-anchor="end"
          font-size="10"
          fill="hsl(var(--fg-subtle))"
        >
          {{ g.leftLabel }}
        </text>
        <text
          v-for="(g, i) in gridLines"
          :key="`r${i}`"
          :x="PAD.left + plotW + 8"
          :y="g.y + 3"
          text-anchor="start"
          font-size="10"
          fill="hsl(var(--fg-subtle))"
        >
          {{ g.rightLabel }}
        </text>
      </g>

      <!-- axis unit captions — 右侧锚定 SVG 右缘 (v3.13.92 fix: start 锚点
           画在 plotW+8 会被十字线/右标签列挤压裁切) -->
      <text :x="PAD.left - 8" :y="PAD.top - 5" text-anchor="end" font-size="10" fill="hsl(var(--brand-500))">
        MB/s
      </text>
      <text
        :x="VB_W - 8"
        :y="PAD.top - 5"
        text-anchor="end"
        font-size="10"
        fill="hsl(var(--success))"
      >
        MB/s
      </text>

      <!-- x ticks -->
      <text
        v-for="(t, i) in xTicks"
        :key="`x${i}`"
        :x="t.x"
        :y="PAD.top + plotH + 16"
        :text-anchor="i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'"
        font-size="10"
        fill="hsl(var(--fg-subtle))"
      >
        {{ t.label }}
      </text>

      <!-- series: upload behind, download in front -->
      <path v-if="upArea" :d="upArea" fill="url(#sc-up-grad)" />
      <path
        v-if="upLine"
        :d="upLine"
        fill="none"
        stroke="hsl(var(--success))"
        stroke-width="1.75"
        stroke-linejoin="round"
        stroke-linecap="round"
        vector-effect="non-scaling-stroke"
      />
      <path v-if="downArea" :d="downArea" fill="url(#sc-down-grad)" />
      <path
        v-if="downLine"
        :d="downLine"
        fill="none"
        stroke="hsl(var(--brand-500))"
        stroke-width="2"
        stroke-linejoin="round"
        stroke-linecap="round"
        vector-effect="non-scaling-stroke"
      />

      <!-- crosshair -->
      <g v-if="hovered">
        <line
          :x1="hovered.x"
          :x2="hovered.x"
          :y1="PAD.top"
          :y2="PAD.top + plotH"
          stroke="hsl(var(--fg-subtle))"
          stroke-width="1"
          stroke-dasharray="3 3"
          vector-effect="non-scaling-stroke"
        />
        <circle :cx="hovered.x" :cy="hovered.downY" r="3" fill="hsl(var(--brand-500))" />
        <circle :cx="hovered.x" :cy="hovered.upY" r="3" fill="hsl(var(--success))" />
      </g>
    </svg>

    <!-- HTML tooltip (kept out of the SVG so text scaling stays crisp)
         v3.13.92 fix: top-9 — 原先 top-3 翻转到左侧时正好压住左上
         "MB/s" 轴单位标题 (y≈11px) -->
    <div
      v-if="hovered"
      class="pointer-events-none absolute top-9 z-10 rounded-md border border-border bg-bg-elevated px-2.5 py-1.5 text-xs shadow-md"
      :style="hovered.flip ? { left: '0.75rem' } : { right: '0.75rem' }"
    >
      <div class="mb-1 font-medium text-fg-muted">{{ hovered.time }}</div>
      <div class="flex items-center gap-1.5 text-fg">
        <span class="h-2 w-2 rounded-full bg-brand-500" />
        <span>{{ downLabel }}</span>
        <span class="ml-auto pl-3 font-medium tabular-nums">{{ hovered.down }}</span>
      </div>
      <div class="mt-0.5 flex items-center gap-1.5 text-fg">
        <span class="h-2 w-2 rounded-full bg-success" />
        <span>{{ upLabel }}</span>
        <span class="ml-auto pl-3 font-medium tabular-nums">{{ hovered.up }}</span>
      </div>
    </div>
    </div>
  </div>
</template>
